import type { PortalRole } from '@ucc/shared';

export type NavItem = {
  id: string;
  label: string;
  href: string;
  /** Lucide-style simple path icon key */
  icon: 'grid' | 'devices' | 'groups' | 'clients' | 'users' | 'user' | 'ota' | 'audit' | 'settings' | 'inbox';
  roles: PortalRole[];
};

export type AppRoute =
  | { name: 'dashboard' }
  | { name: 'devices' }
  | { name: 'claim-device' }
  | { name: 'register-device' }
  | { name: 'device'; deviceId: string }
  | { name: 'groups' }
  | { name: 'group'; groupId: string }
  | { name: 'clients' }
  | { name: 'staff' }
  | { name: 'ota' }
  | { name: 'audit' }
  | { name: 'settings' }
  | { name: 'sku-manager' }
  | { name: 'profiles' }
  | { name: 'tenants' }
  | { name: 'organization' }
  | { name: 'upstream-formats' }
  | { name: 'connections' }
  | { name: 'support-access' }
  | { name: 'device-pools' }
  | { name: 'device-pool'; poolId: string }
  | { name: 'ingress-connections' }
  | { name: 'command-sets' }
  | { name: 'invite'; token: string }
  | { name: 'account' }
  | { name: 'unknown'; path: string };

/**
 * Portal nav is role-filtered. Add routes here as features land —
 * shell stays stable while pages grow.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    href: '#/dashboard',
    icon: 'grid',
    roles: ['admin', 'user', 'client'],
  },
  {
    id: 'devices',
    label: 'Devices',
    href: '#/devices',
    icon: 'devices',
    roles: ['admin', 'user', 'client'],
  },
  {
    id: 'groups',
    label: 'Groups',
    href: '#/groups',
    icon: 'groups',
    roles: ['client'],
  },
  {
    id: 'device-pools',
    label: 'Device Pool',
    href: '#/device-pools',
    icon: 'groups',
    roles: ['admin', 'user'],
  },
  {
    id: 'ota',
    label: 'OTA',
    href: '#/ota',
    icon: 'ota',
    roles: ['admin', 'user'],
  },
  {
    id: 'clients',
    label: 'Clients',
    href: '#/clients',
    icon: 'clients',
    roles: ['admin', 'user'],
  },
  {
    id: 'settings',
    label: 'Settings',
    href: '#/settings',
    icon: 'settings',
    roles: ['admin', 'user', 'client'],
  },
  {
    id: 'audit',
    label: 'Audit',
    href: '#/audit',
    icon: 'audit',
    roles: ['admin', 'user', 'client'],
  },
];

export function navForRole(role: PortalRole): NavItem[] {
  return NAV_ITEMS.filter((item) => item.roles.includes(role));
}

/** Top-level segment for shell active state (device/group detail → parent). */
export function parseHashRoute(hash: string): string {
  const r = parseAppRoute(hash);
  if (r.name === 'device' || r.name === 'claim-device' || r.name === 'register-device') return 'devices';
  if (r.name === 'group') return 'groups';
  if (r.name === 'device-pool') return 'device-pools';
  if (
    r.name === 'sku-manager' ||
    r.name === 'profiles' ||
    r.name === 'tenants' ||
    r.name === 'organization' ||
    r.name === 'upstream-formats' ||
    r.name === 'connections' ||
    r.name === 'support-access' ||
    r.name === 'ingress-connections' ||
    r.name === 'command-sets' ||
    r.name === 'account' ||
    r.name === 'staff'
  ) {
    return 'settings';
  }
  if (r.name === 'unknown') return 'dashboard';
  return r.name;
}

export function parseAppRoute(hash: string): AppRoute {
  const raw = hash.replace(/^#\/?/, '').trim();
  if (!raw) return { name: 'dashboard' };

  const path = raw.split(/[?#]/)[0] ?? '';

  if (path === 'account' || path.startsWith('account/')) return { name: 'account' };
  if (path === 'devices/claim' || path.startsWith('devices/claim/')) return { name: 'claim-device' };
  if (path === 'devices/register' || path.startsWith('devices/register/')) return { name: 'register-device' };
  if (path === 'settings/skus' || path.startsWith('settings/skus/')) return { name: 'sku-manager' };
  if (path === 'settings/profiles' || path.startsWith('settings/profiles/')) {
    return { name: 'profiles' };
  }
  if (path === 'settings/tenants' || path.startsWith('settings/tenants/')) {
    return { name: 'tenants' };
  }
  if (path === 'settings/organization' || path.startsWith('settings/organization/')) {
    return { name: 'organization' };
  }
  if (path === 'settings/formats' || path.startsWith('settings/formats/')) {
    return { name: 'upstream-formats' };
  }
  if (path === 'settings/connections' || path.startsWith('settings/connections/')) {
    return { name: 'connections' };
  }
  if (path === 'settings/support' || path.startsWith('settings/support/')) {
    return { name: 'support-access' };
  }
  if (path === 'device-pools') {
    return { name: 'device-pools' };
  }
  if (path.startsWith('device-pools/')) {
    const poolId = path.slice('device-pools/'.length);
    return poolId ? { name: 'device-pool', poolId: decodeURIComponent(poolId) } : { name: 'device-pools' };
  }
  if (path === 'settings/ingress-connections' || path.startsWith('settings/ingress-connections/')) {
    return { name: 'ingress-connections' };
  }
  if (path === 'settings/command-sets' || path.startsWith('settings/command-sets/')) {
    return { name: 'command-sets' };
  }
  const parts = path.split('/').filter(Boolean);
  const head = parts[0] ?? 'dashboard';

  if (head === 'invite' && parts[1]) {
    return { name: 'invite', token: decodeURIComponent(parts[1]) };
  }
  if (head === 'devices' && parts[1]) {
    return { name: 'device', deviceId: decodeURIComponent(parts[1]) };
  }
  if (head === 'groups' && parts[1]) {
    return { name: 'group', groupId: decodeURIComponent(parts[1]) };
  }

  switch (head) {
    case 'dashboard':
    case 'devices':
    case 'groups':
    case 'clients':
    case 'staff':
    case 'ota':
    case 'audit':
    case 'settings':
      return { name: head };
    default:
      return { name: 'unknown', path: head };
  }
}

export function routeLabel(route: AppRoute): string {
  if (route.name === 'account') return 'Account';
  switch (route.name) {
    case 'dashboard':
      return 'Dashboard';
    case 'devices':
      return 'Devices';
    case 'claim-device':
      return 'Claim device';
    case 'register-device':
      return 'Register device';
    case 'device':
      return 'Device';
    case 'groups':
      return 'Groups';
    case 'group':
      return 'Group';
    case 'clients':
      return 'Clients';
    case 'staff':
      return 'Staff users';
    case 'ota':
      return 'OTA';
    case 'audit':
      return 'Audit';
    case 'settings':
      return 'Settings';
    case 'sku-manager':
      return 'SKU Manager';
    case 'profiles':
      return 'Profiles';
    case 'tenants':
      return 'Tenants';
    case 'organization':
      return 'Organization';
    case 'upstream-formats':
      return 'Upstream Formats';
    case 'connections':
      return 'Connections';
    case 'support-access':
      return 'Support Access';
    case 'device-pools':
      return 'Device Pool';
    case 'device-pool':
      return 'Device Pool';
    case 'ingress-connections':
      return 'Ingress Connection Methods';
    case 'command-sets':
      return 'Command Sets';
    default:
      return 'Page';
  }
}

export function deviceHref(id: string): string {
  return `#/devices/${encodeURIComponent(id)}`;
}

export function claimDeviceHref(): string {
  return '#/devices/claim';
}

export function registerDeviceHref(): string {
  return '#/devices/register';
}

export function groupHref(id: string): string {
  return `#/groups/${encodeURIComponent(id)}`;
}
